'use client';

import { useState } from 'react';
import type { HomeValuationBlockProps } from '../types';
import { theme, sectionColors, buttonVars } from '../theme';
import { SpamGuardInputs, useSpamGuard } from './useSpamGuard';
import { VALUATION_SMS_CONSENT_TEXT, approvedSmsConsentText, smsConsentFields } from './smsConsent';

/**
 * Home valuation — the V3 four-step capture (docs/V3_LAUNCH_PLAN.md §3.2). CLIENT component,
 * framework-agnostic (plain elements, no next/*):
 *
 *   1. address (+ optional beds/baths/sqft)      → POST same-origin /api/valuation/teaser
 *   2. teaser card: "$580K – $6XX,XXX" + unlock CTA
 *   3. the gate: name, email, phone (required), an UNCHECKED SMS-consent checkbox that is separate
 *      from the submit button and never pre-ticked (TCPA)   → POST /api/valuation
 *   4. inline result: range, confidence sentence, comps count, "Full report sent to {email}" + link
 *
 * V3.1 (docs/V3_LAUNCH_PLAN.md §4):
 *   - PURL direct mail (11): `initialAddress` + `initialTeaser` — the personal page /v/:token has
 *     the address and the blurred range pre-computed by the mailing batch, so the block opens on
 *     step 2 with nothing to type; the gate unlocks with the teaser's token.
 *   - Zestimate-check (13): `mode="zestimate_check"` adds "What does Zillow say your home is
 *     worth?" (optional) to step 1 — and to the gate when it was not asked yet — and sends it as
 *     `zestimate`; the full report shows Zillow's number against the local sold comps.
 *
 * The lead/valuation destination is resolved server-side per tenant — never hardcoded here
 * (CLAUDE.md non-negotiable #2). Analytics: NONE from this block — the gateway records
 * `valuation_teaser` and `valuation_requested` itself when it handles the two POSTs, and a
 * second client-side copy would double-count them in the intent scores.
 */

/**
 * Default TCPA consent checkbox copy — version `v1-2026-09` (docs/SMS_CONSENT.md). The wording
 * and its version are the platform-owned contract in ./smsConsent.ts: an agent-configured
 * `consentText` is only rendered when it is one of the approved wordings, and the words shown
 * are posted with the capture so the gateway derives the stored version id from them.
 */
export const DEFAULT_SMS_CONSENT_TEXT = VALUATION_SMS_CONSENT_TEXT;

export const ZESTIMATE_FIELD_LABEL = 'What does Zillow say your home is worth?';

interface Teaser {
  low_label: string;
  high_masked: string;
  confidence: 'high' | 'medium' | 'low';
  comp_count: number;
}

interface TeaserResponse {
  data?: { token: string | null; teaser: Teaser | null; reason?: string } | null;
  error?: string | null;
}

interface FullEstimate {
  low: number;
  mid: number;
  high: number;
  confidence: 'high' | 'medium' | 'low';
  comp_count: number;
  confidence_sentence?: string;
  /** The first few comps (POST /valuation) — thumbnails on the inline result. */
  comps?: Array<{ address: string; city: string | null; closePrice: number; photoUrl: string | null }>;
}

interface FullResponse {
  data?: { id: string; token: string | null; estimate: FullEstimate | null; report_url: string | null } | null;
  error?: string | null;
}

type Step = 'address' | 'teaser' | 'gate' | 'result';

interface Subject { address: string; beds?: number; baths?: number; sqft?: number; zestimate?: number }

function readBrowserId(): string | undefined {
  try {
    const m = typeof document !== 'undefined' ? document.cookie.match(/(?:^|;\s*)blt_bid=([^;]+)/) : null;
    return m ? decodeURIComponent(m[1]) : undefined;
  } catch {
    return undefined;
  }
}

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;

function numberOrUndefined(raw: FormDataEntryValue | null): number | undefined {
  const s = String(raw ?? '').trim();
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** "$650,000" / "650k" / "650000" → 650000; anything else → undefined. Exported for tests. */
export function parseZestimate(raw: FormDataEntryValue | null): number | undefined {
  const s = String(raw ?? '').trim().toLowerCase().replace(/[$,\s]/g, '');
  if (!s) return undefined;
  const m = /^(\d+(?:\.\d+)?)(k|m)?$/.exec(s);
  if (!m) return undefined;
  const n = Number(m[1]) * (m[2] === 'k' ? 1_000 : m[2] === 'm' ? 1_000_000 : 1);
  return Number.isFinite(n) && n > 0 && n <= 50_000_000 ? Math.round(n) : undefined;
}

/**
 * The one-line Zestimate comparison on the inline result (the report carries the full copy).
 * Fixed strings, no LLM. Exported for tests.
 */
export function zestimateDeltaLine(zestimate: number, estimate: Pick<FullEstimate, 'low' | 'high' | 'comp_count'>): string {
  const sales = `${estimate.comp_count} recent ${estimate.comp_count === 1 ? 'sale' : 'sales'} nearby`;
  if (zestimate < estimate.low) return `Zillow says ${money(zestimate)} — ${money(estimate.low - zestimate)} below the low end of what ${sales} support.`;
  if (zestimate > estimate.high) return `Zillow says ${money(zestimate)} — ${money(zestimate - estimate.high)} above the top of what ${sales} support.`;
  return `Zillow says ${money(zestimate)} — inside the range ${sales} support. The full report shows where.`;
}

export function HomeValuationBlock(props: HomeValuationBlockProps & { turnstileSiteKey?: string | null }) {
  const isZestimate = props.mode === 'zestimate_check';
  const {
    heading = isZestimate ? 'Is your Zestimate right?' : "What's your home worth?",
    subheading,
    buttonText = isZestimate ? 'Check it against real sales' : 'Get my estimate',
    showPhone = true,
    consentText,
    phoneRequired = true,
    teaserText = 'Your home is likely worth',
    initialAddress,
    initialTeaser,
  } = props;
  // The section's button colours ride on the section as --blk-btn vars (plan E6).
  const colors = { ...sectionColors(props, { bg: 'transparent', text: theme.text }), ...buttonVars(props) };
  // Platform-owned TCPA copy: an unapproved (agent-edited) wording is never shown, because it
  // could not be recorded against a version id (./smsConsent.ts).
  const shownConsentText = approvedSmsConsentText(consentText, VALUATION_SMS_CONSENT_TEXT);
  const field = 'w-full rounded-md border border-black/15 bg-transparent px-3 py-2';
  const primaryBtn = 'rounded-md px-6 py-2 font-medium hover:opacity-90 disabled:opacity-60';

  // Spam-check inputs for the gate (./useSpamGuard.tsx).
  const guard = useSpamGuard(props.turnstileSiteKey);
  // A PURL landing page already has the teaser: open on step 2, nothing to type.
  const [step, setStep] = useState<Step>(() => (initialTeaser ? 'teaser' : 'address'));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sphere "want in?" emails (lib/sphere.ts) link here with ?address= prefilled from the agent's
  // contact list, so the person only confirms it; a PURL passes it as a prop. Read once at mount;
  // SSR renders '' and React sets defaultValue on hydration, so the pristine input shows the
  // address either way.
  const [subject, setSubject] = useState<Subject>(() => ({
    address: (initialAddress ?? (typeof window === 'undefined' ? '' : (new URLSearchParams(window.location.search).get('address') ?? ''))).trim().slice(0, 300),
  }));
  const [token, setToken] = useState<string | null>(() => initialTeaser?.token ?? null);
  const [teaser, setTeaser] = useState<Teaser | null>(() => (initialTeaser ? { ...initialTeaser, comp_count: initialTeaser.comp_count ?? 0 } : null));
  const [consent, setConsent] = useState(false);
  const [result, setResult] = useState<{ email: string; estimate: FullEstimate | null; reportUrl: string | null; zestimate?: number } | null>(null);

  async function handleAddress(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const fd = new FormData(e.currentTarget);
    const next: Subject = {
      address: String(fd.get('address') ?? '').trim(),
      beds: numberOrUndefined(fd.get('beds')),
      baths: numberOrUndefined(fd.get('baths')),
      sqft: numberOrUndefined(fd.get('sqft')),
      ...(isZestimate ? { zestimate: parseZestimate(fd.get('zestimate')) } : {}),
    };
    if (!next.address) return;
    setSubject(next);
    setError(null);
    setPending(true);
    try {
      const res = await fetch('/api/valuation/teaser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...next, browser_id: readBrowserId(), page_url: typeof window !== 'undefined' ? window.location.href : undefined }),
      });
      const body = (await res.json().catch(() => ({}))) as TeaserResponse;
      if (!res.ok) throw new Error(body.error || `Request failed: ${res.status}`);
      const data = body.data;
      if (data?.token && data.teaser) {
        setToken(data.token);
        setTeaser(data.teaser);
        setStep('teaser');
      } else {
        // Nothing to tease with (no nearby sales yet) — go straight to the gate; the agent follows up.
        setToken(null);
        setTeaser(null);
        setStep('gate');
      }
    } catch {
      setError('Sorry, we could not look up that address. Please check it and try again.');
    } finally {
      setPending(false);
    }
  }

  async function handleGate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const fd = new FormData(e.currentTarget);
    const name = String(fd.get('name') ?? '').trim();
    const email = String(fd.get('email') ?? '').trim();
    const phone = String(fd.get('phone') ?? '').trim();
    const phoneDigits = phone.replace(/\D/g, '').length;
    if (phoneDigits < 7 && (phoneRequired || phoneDigits > 0)) {
      setError(phoneRequired ? 'Please enter a phone number so we can send your full report.' : 'Please enter a valid phone number, or leave it blank.');
      return;
    }
    // Zestimate mode on a PURL page: step 1 was skipped, so the gate asks once.
    const zestimate = subject.zestimate ?? (isZestimate && fd.has('zestimate') ? parseZestimate(fd.get('zestimate')) : undefined);
    const spam = guard.fields(e.currentTarget);
    setError(null);
    setPending(true);
    try {
      const res = await fetch('/api/valuation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token ?? undefined,
          ...subject,
          zestimate,
          name,
          email,
          phone: phone || undefined,
          // Mirrors the prop so the proxy + gateway accept an empty phone only when the agent chose that.
          phone_required: phoneRequired,
          // Consent is for a number: without one there is nothing to consent to. The exact words
          // shown travel with it; the gateway derives the stored version id from them.
          ...smsConsentFields({ checked: consent, hasPhone: phoneDigits > 0, shownText: shownConsentText }),
          browser_id: readBrowserId(),
          page_url: typeof window !== 'undefined' ? window.location.href : undefined,
          ...spam,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as FullResponse;
      if (!res.ok) throw new Error(body.error || `Request failed: ${res.status}`);
      const data = body.data;
      setResult({ email, estimate: data?.estimate ?? null, reportUrl: data?.report_url ?? null, zestimate });
      setStep('result');
    } catch (err) {
      setError(err instanceof Error && err.message && !/Request failed/.test(err.message) ? err.message : 'Sorry, something went wrong sending your report. Please try again.');
    } finally {
      guard.reset();
      setPending(false);
    }
  }

  const errorBox = error ? (
    <p className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{error}</p>
  ) : null;

  const zestimateInput = (
    <label className="block text-sm">
      <span className="mb-1 block opacity-80">{ZESTIMATE_FIELD_LABEL} <span className="opacity-60">(optional)</span></span>
      <input type="text" inputMode="numeric" name="zestimate" placeholder="e.g. $650,000" className={field} data-testid="zestimate-input" />
    </label>
  );

  return (
    <section id="home-valuation" className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }} data-mode={isZestimate ? 'zestimate_check' : 'standard'}>
      <div className="mx-auto max-w-xl">
        <h2 className="mb-2 text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        {subheading ? <p className="mb-6 opacity-80">{subheading}</p> : null}

        {step === 'address' ? (
          <>
            {errorBox}
            <form onSubmit={handleAddress} className="space-y-4" data-step="address">
              <input type="text" name="address" placeholder="Property address" required autoComplete="street-address" className={field} defaultValue={subject.address} />
              <div className="grid grid-cols-3 gap-3">
                <input type="number" name="beds" placeholder="Beds" min={0} step={1} className={field} />
                <input type="number" name="baths" placeholder="Baths" min={0} step={0.5} className={field} />
                <input type="number" name="sqft" placeholder="Sq ft" min={0} className={field} />
              </div>
              {isZestimate ? zestimateInput : null}
              <button type="submit" disabled={pending} className={`blk-btn ${primaryBtn}`}>
                {pending ? 'Checking recent sales…' : buttonText}
              </button>
            </form>
          </>
        ) : null}

        {step === 'teaser' && teaser ? (
          <div className="rounded-lg border border-black/10 p-6" data-step="teaser">
            <p className="text-sm opacity-70">{subject.address}</p>
            <p className="mt-2 text-sm opacity-80">{teaserText}</p>
            <p className="mt-1 text-3xl font-semibold" style={{ fontFamily: theme.headingFont }}>
              {teaser.low_label} – <span aria-label="upper range hidden until you unlock the report">{teaser.high_masked}</span>
            </p>
            {teaser.comp_count > 0 ? (
              <p className="mt-2 text-sm opacity-70">
                Based on {teaser.comp_count} recent nearby {teaser.comp_count === 1 ? 'sale' : 'sales'} from the local MLS.
              </p>
            ) : (
              <p className="mt-2 text-sm opacity-70">Based on recent nearby sales from the local MLS.</p>
            )}
            <button type="button" onClick={() => { setError(null); setStep('gate'); }} className={`blk-btn ${primaryBtn} mt-5`}>
              Unlock the full report
            </button>
            <button type="button" onClick={() => setStep('address')} className="ml-3 text-sm underline opacity-70">Change address</button>
          </div>
        ) : null}

        {step === 'gate' ? (
          <>
            {teaser ? (
              <p className="mb-4 text-sm opacity-80">
                {subject.address}: <strong>{teaser.low_label} – {teaser.high_masked}</strong>. Tell us where to send the full report.
              </p>
            ) : (
              <p className="mb-4 text-sm opacity-80">
                We could not find enough recent sales near {subject.address} for an instant number. Leave your details and your agent will prepare a valuation for you.
              </p>
            )}
            {errorBox}
            <form onSubmit={handleGate} className="space-y-4" data-step="gate">
              <input type="text" name="name" placeholder="Your name" required autoComplete="name" className={field} />
              <input type="email" name="email" placeholder="you@example.com" required autoComplete="email" className={field} />
              {showPhone || phoneRequired ? (
                <>
                  <input type="tel" name="phone" placeholder={phoneRequired ? 'Phone' : 'Phone (optional)'} required={phoneRequired} autoComplete="tel" className={field} />
                  <label className="flex items-start gap-2 text-sm opacity-90">
                    <input
                      type="checkbox"
                      name="consent_sms"
                      checked={consent}
                      onChange={(e) => setConsent(e.currentTarget.checked)}
                      className="mt-1"
                    />
                    <span>{shownConsentText}</span>
                  </label>
                </>
              ) : null}
              {isZestimate && subject.zestimate === undefined ? zestimateInput : null}
              <SpamGuardInputs guard={guard} />
              <button type="submit" disabled={pending} className={`blk-btn ${primaryBtn}`}>
                {pending ? 'Sending your report…' : 'Send my full report'}
              </button>
              <button type="button" onClick={() => setStep(teaser ? 'teaser' : 'address')} className="ml-3 text-sm underline opacity-70">Back</button>
            </form>
          </>
        ) : null}

        {step === 'result' && result ? (
          <div className="rounded-md bg-emerald-50 px-4 py-4 text-sm text-emerald-900" data-step="result">
            {result.estimate ? (
              <>
                <p className="text-xs uppercase tracking-wide opacity-70">{subject.address}</p>
                <p className="mt-1 text-2xl font-semibold">{money(result.estimate.low)} – {money(result.estimate.high)}</p>
                {result.estimate.confidence_sentence ? <p className="mt-1">{result.estimate.confidence_sentence}</p> : null}
                <p className="mt-1 opacity-80">Based on {result.estimate.comp_count} comparable {result.estimate.comp_count === 1 ? 'sale' : 'sales'}.</p>
                {result.zestimate ? <p className="mt-2 font-medium" data-testid="zestimate-delta">{zestimateDeltaLine(result.zestimate, result.estimate)}</p> : null}
                {(result.estimate.comps ?? []).some((c) => c.photoUrl) ? (
                  <ul className="mt-3 flex flex-wrap gap-2" data-testid="valuation-comp-photos">
                    {(result.estimate.comps ?? []).filter((c) => c.photoUrl).slice(0, 3).map((c, i) => (
                      <li key={`${c.address}-${i}`} className="w-28">
                        <img src={c.photoUrl ?? undefined} alt={c.address} loading="lazy" width={112} height={80} className="h-20 w-28 rounded-md object-cover" />
                        <p className="mt-1 truncate text-xs opacity-80" title={c.address}>{money(c.closePrice)} · {c.address}</p>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : (
              <p className="font-medium">Thanks — your agent will prepare a valuation for {subject.address} and be in touch.</p>
            )}
            <p className="mt-3">Full report sent to <strong>{result.email}</strong>.</p>
            {result.reportUrl ? (
              <p className="mt-2">
                <a href={result.reportUrl} className="font-medium underline">View your full report</a>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
